// PIP-3358: keep the canonical /{brand}-{style}.html PDP URL across color
// selection on the blanks/?ac= surface. The selected color is carried in a
// `#color` hash fragment (written on select, read on load to pre-select)
// instead of rewriting the ?ac= query param. Crawlers ignore the hash, so the
// canonical URL stays the single indexable URL for the product.
import { parameterizeColor } from "./enhanced-url-generator";

const PRODUCT_PAGE_CONTAINER_ID = "product-page-container";

const getProductPageDataset = (): DOMStringMap | undefined =>
  document.getElementById(PRODUCT_PAGE_CONTAINER_ID)?.dataset;

const readFlagFromDataset = (): boolean => getProductPageDataset()?.["colorUrlPersist"] === "true";

const readFlagFromWindow = (): boolean => window?.jiffy?.flip?.colorUrlPersist === true;

/**
 * Whether the PDP should persist the canonical URL and carry color via #color.
 */
export const isColorUrlPersistEnabled = (): boolean =>
  readFlagFromDataset() || readFlagFromWindow();

/**
 * Build an absolute URL for the current page with the selected color carried in
 * a clean `?ac=<color>` query param (any stale `ac` replaced), for server-to-server
 * payloads like the Meta CAPI source_url. Meta strips URL fragments and the payload
 * is never crawled, so a `#color` hash buys no SEO there — a clean `?ac=` matches the
 * product feed and resolves the correct color. Returns the current path + query
 * unchanged when the color is blank.
 */
export const buildCanonicalColorParamUrl = (colorName: string): string => {
  const { origin, pathname, search } = window.location;
  const params = new URLSearchParams(search);
  if (!colorName) return `${origin}${pathname}${params.toString() ? `?${params.toString()}` : ""}`;

  params.set("ac", colorName);
  return `${origin}${pathname}?${params.toString()}`;
};

// Query params that mark a made/sample/edit/flash color link — those anchors
// must keep navigating to their custom URL, never collapse to a hash rewrite.
const MADE_CONTEXT_PARAMS = ["sample", "asgid", "sameday", "made", "print"];

/**
 * Whether a color anchor targets the CURRENT canonical PDP (blank surface) —
 * the only links the hash-only rewrite may replace. Legacy made sample/edit/
 * flash PDPs render swatches via customizable_product_path with made-context
 * params but without data-made-dedicated-pdp, so the writer branches cannot
 * rely on the dataset alone.
 */
export const isCanonicalPdpColorLink = (anchor: {
  pathname?: string;
  search?: string;
}): boolean => {
  if (!anchor?.pathname || anchor.pathname !== window.location.pathname) return false;

  const params = new URLSearchParams(anchor.search || "");
  return !MADE_CONTEXT_PARAMS.some(param => params.has(param));
};

/**
 * Write the selected color to location.hash, preserving pathname + query string.
 * Uses replaceState so color cycling does not pollute the history stack.
 *
 * Slug parity: the hash written here (and matched in preselect) uses
 * `parameterizeColor`, while server-emitted crawlable links use Ruby's
 * `String#parameterize` (`crawlable_product_color_path` in
 * packages/catalog/app/helpers/spree/products_helper_decorator.rb). The two
 * slugifiers MUST stay in sync — they agree on ASCII and common accents
 * (NFKD-strip vs I18n transliteration both yield "cafe" for "Café"), and a
 * divergent name degrades gracefully to the default color. If you change one
 * slugifier, change the other.
 */
export const setColorHash = (colorName: string): void => {
  const slug = parameterizeColor(colorName);
  if (!slug) return;

  // Strip any stale ?ac= so the canonical surface never ends up with both a
  // query color and a hash color (e.g. ?ac=Red#blue) after deep-linking.
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  params.delete("ac");
  const query = params.toString();
  const newUrl = `${pathname}${query ? `?${query}` : ""}#${slug}`;
  window.history.replaceState(null, "", newUrl);
};

/**
 * Read the color slug from location.hash and de-slugify it back to a space
 * separated name suitable for matching against `data-color` values.
 * @returns the de-slugified color name, or null when no hash is present.
 */
export const getColorFromHash = (): string | null => {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;

  // A hash that targets a real on-page element (#comments, #similar-items, …)
  // is in-page navigation, not a color share. Excluding it HERE means every
  // read site (slider pre-select, pdp-data-collection, delivery-date
  // composition resolution) shares the exclusion instead of re-implementing it.
  if (document.getElementById(hash)) return null;

  // decodeURIComponent throws on malformed escape sequences (e.g. a bare `%`).
  // Fall back to the raw hash so a hand-edited URL never breaks color reads.
  try {
    return decodeURIComponent(hash).replace(/-/g, " ");
  } catch {
    return hash.replace(/-/g, " ");
  }
};
